import { forwardRef, useImperativeHandle, useRef, useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import TurndownService from "turndown";
import api from "@/lib/api";
import {
  MDXEditor, headingsPlugin, listsPlugin, quotePlugin, thematicBreakPlugin,
  markdownShortcutPlugin, linkPlugin, linkDialogPlugin, imagePlugin, toolbarPlugin,
  UndoRedo, BoldItalicUnderlineToggles, BlockTypeSelect, CreateLink, ListsToggle,
  InsertImage, Separator, realmPlugin, addLexicalNode$, createRootEditorSubscription$,
} from "@mdxeditor/editor";
import { TextNode, $getRoot, $getSelection, $isRangeSelection, $createTextNode } from "lexical";
import "@mdxeditor/editor/style.css";

const turndown = new TurndownService({ headingStyle: "atx", bulletListMarker: "-", codeBlockStyle: "fenced" });
const fold = (s) => (s || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

// Liste courante des titres de tags (triés du plus long au plus court) + éditeur Lexical racine.
// Partagés au niveau module car le plugin MDXEditor est un singleton.
let CURRENT_TAGS = [];
let ROOT_EDITOR = null;
const isTagChar = (c) => !!c && /[\p{L}0-9/_-]/u.test(c);

// Nœud « bulle » couvrant l'intégralité du tag (y compris espaces et « / »).
class SxTagNode extends TextNode {
  static getType() { return "sx-tag"; }
  static clone(node) { return new SxTagNode(node.__text, node.__key); }
  static importJSON(s) { const n = new SxTagNode(s.text || ""); n.setFormat(s.format || 0); n.setStyle(s.style || ""); return n; }
  exportJSON() { return { ...super.exportJSON(), type: "sx-tag", version: 1 }; }
  createDOM(config) { const dom = super.createDOM(config); dom.classList.add("sx-hashtag"); return dom; }
  isTextEntity() { return true; }
  canInsertTextBefore() { return false; }
}

// Transforme « #<titre de tag connu> » en bulle, quel que soit l'espace / le « / » dans le titre.
function registerSxTags(editor) {
  return editor.registerNodeTransform(TextNode, (node) => {
    if (node instanceof SxTagNode || !node.isSimpleText()) return;
    const text = node.getTextContent();
    if (text.indexOf("#") < 0 || !CURRENT_TAGS.length) return;
    for (const titre of CURRENT_TAGS) {
      const needle = "#" + titre;
      let from = 0, idx;
      while ((idx = text.indexOf(needle, from)) >= 0) {
        const before = idx > 0 ? text[idx - 1] : "";
        const after = text[idx + needle.length] || "";
        if ((!before || /\s/.test(before)) && !isTagChar(after)) {
          let target = node;
          if (idx > 0) target = target.splitText(idx)[1];
          const parts = target.splitText(needle.length);
          const match = parts[0];
          match.replace(new SxTagNode(needle));
          return; // un remplacement par passe ; la transformation se relance
        }
        from = idx + 1;
      }
    }
  });
}

const tagBubblePlugin = realmPlugin({
  init(realm) {
    realm.pubIn({
      [addLexicalNode$]: SxTagNode,
      [createRootEditorSubscription$]: (rootEditor) => { ROOT_EDITOR = rootEditor; return registerSxTags(rootEditor); },
    });
  },
});

// À la copie, on place le markdown (mise en forme conservée) dans le presse-papier.
function handleCopy(e) {
  try {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
    const div = document.createElement("div");
    for (let i = 0; i < sel.rangeCount; i++) div.appendChild(sel.getRangeAt(i).cloneContents());
    const md = turndown.turndown(div.innerHTML);
    if (md) { e.clipboardData.setData("text/plain", md); e.preventDefault(); }
  } catch (_) { /* noop */ }
}

async function imageUploadHandler(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/settings/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.url;
}

// Éditeur markdown WYSIWYG (type Obsidian) : mise en forme en direct,
// liens cliquables, collage de markdown/HTML converti, autocomplétion #tag (bulles).
export const RichMarkdownEditor = forwardRef(({ markdown, onChange, tags = [] }, ref) => {
  const mdxRef = useRef(null);
  const wrapRef = useRef(null);
  const [menu, setMenu] = useState(null); // { query, items, index, x, y }
  const menuRef = useRef(null);
  menuRef.current = menu;
  const dismissedRef = useRef(null);

  useImperativeHandle(ref, () => ({
    insertMarkdown: (md) => mdxRef.current?.insertMarkdown(md),
    setMarkdown: (md) => mdxRef.current?.setMarkdown(md),
    getMarkdown: () => mdxRef.current?.getMarkdown(),
    focus: (cb, opts) => mdxRef.current?.focus?.(cb, opts),
  }), []);

  // Met à jour la liste de tags connue du transformateur et re-scanne le contenu existant.
  useEffect(() => {
    CURRENT_TAGS = [...tags.map((t) => t.titre)].filter(Boolean).sort((a, b) => b.length - a.length);
    if (ROOT_EDITOR) {
      try { ROOT_EDITOR.update(() => { $getRoot().getAllTextNodes().forEach((n) => n.markDirty()); }); } catch (_) { /* noop */ }
    }
  }, [tags]);

  const close = useCallback(() => setMenu(null), []);

  const detect = useCallback(() => {
    if (!tags.length) return close();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return close();
    const node = sel.anchorNode;
    if (!node || node.nodeType !== 3 || !wrapRef.current?.contains(node)) return close();
    const before = node.textContent.slice(0, sel.anchorOffset);
    const m = before.match(/(?:^|\s)#([\p{L}0-9_/-]*)$/u);
    if (!m) { dismissedRef.current = null; return close(); }
    const query = m[1];
    if (dismissedRef.current !== null && dismissedRef.current === query) return close();
    if (dismissedRef.current !== null) dismissedRef.current = null;
    const fq = fold(query);
    const items = tags.filter((t) => fold(t.titre).includes(fq)).slice(0, 8);
    if (!items.length) return close();
    // Position : juste sous la ligne courante. Rect calculé du début du nœud jusqu'au curseur
    // (fiable même quand le range réduit renvoie un rect vide dans Chromium).
    const r = document.createRange();
    let rect = null;
    if (sel.anchorOffset > 0) {
      r.setStart(node, 0); r.setEnd(node, sel.anchorOffset);
      const rs = r.getClientRects(); const last = rs[rs.length - 1];
      if (last) rect = { left: last.right, bottom: last.bottom };
    }
    if (!rect && node.textContent.length) {
      r.setStart(node, 0); r.setEnd(node, 1);
      const rs = r.getClientRects(); const first = rs[0];
      if (first) rect = { left: first.left, bottom: first.bottom };
    }
    if (!rect) { const pr = node.parentElement?.getBoundingClientRect(); rect = { left: pr?.left || 0, bottom: (pr?.top || 0) + 22 }; }
    setMenu({ query, items, index: 0, x: rect.left, y: rect.bottom });
  }, [tags, close]);

  const pick = useCallback((tag) => {
    const q = menuRef.current?.query || "";
    close();
    const ed = ROOT_EDITOR;
    if (!ed) return;
    ed.focus();
    // On pilote Lexical directement : supprimer « #<requête> » puis insérer un vrai nœud bulle
    // (couvre les titres avec espaces / « / ») suivi d'une espace.
    ed.update(() => {
      const sel = $getSelection();
      if (!$isRangeSelection(sel)) return;
      for (let i = 0; i < q.length + 1; i++) sel.deleteCharacter(true);
      sel.insertNodes([new SxTagNode("#" + tag.titre), $createTextNode(" ")]);
    });
  }, [close]);

  const onKeyDown = useCallback((e) => {
    const m = menuRef.current;
    if (!m) return;
    if (e.key === "ArrowDown") { e.preventDefault(); e.stopPropagation(); setMenu((c) => c && { ...c, index: Math.min(c.index + 1, c.items.length - 1) }); }
    else if (e.key === "ArrowUp") { e.preventDefault(); e.stopPropagation(); setMenu((c) => c && { ...c, index: Math.max(c.index - 1, 0) }); }
    else if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); e.stopPropagation(); pick(m.items[m.index]); }
    else if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); dismissedRef.current = m.query; close(); }
    else if (e.key === " ") { close(); }
  }, [pick, close]);

  // L'événement 'input' de Lexical ne remonte pas au wrapper : on écoute 'selectionchange'.
  useEffect(() => {
    if (!tags.length) return undefined;
    const h = () => requestAnimationFrame(detect);
    document.addEventListener("selectionchange", h);
    return () => document.removeEventListener("selectionchange", h);
  }, [detect, tags.length]);

  return (
    <div ref={wrapRef} onCopy={handleCopy} onKeyDownCapture={onKeyDown}
      onBlur={() => setTimeout(() => { if (!wrapRef.current?.contains(document.activeElement)) close(); }, 120)}
      style={{ position: "relative" }}>
      <MDXEditor
        ref={mdxRef}
        markdown={markdown || ""}
        onChange={onChange}
        contentEditableClassName="prose prose-sm max-w-none min-h-[480px] px-5 py-4 prose-headings:font-display prose-headings:text-slate-900 prose-a:text-blue-600"
        plugins={[
          toolbarPlugin({
            toolbarContents: () => (
              <>
                <UndoRedo />
                <Separator />
                <BoldItalicUnderlineToggles />
                <Separator />
                <BlockTypeSelect />
                <ListsToggle />
                <Separator />
                <CreateLink />
                <InsertImage />
              </>
            ),
          }),
          headingsPlugin(),
          listsPlugin(),
          quotePlugin(),
          thematicBreakPlugin(),
          linkPlugin(),
          linkDialogPlugin(),
          imagePlugin({ imageUploadHandler }),
          markdownShortcutPlugin(),
          tagBubblePlugin(),
        ]}
      />
      {menu && createPortal((
        <div className="sx-tag-menu" data-testid="tag-autocomplete" style={{ position: "fixed", left: menu.x, top: menu.y + 4, zIndex: 70 }}>
          {menu.items.map((t, i) => (
            <button key={t.id} type="button" data-testid={`tag-ac-item-${i}`}
              onMouseDown={(e) => { e.preventDefault(); pick(t); }}
              onMouseEnter={() => setMenu((c) => c && { ...c, index: i })}
              className="sx-tag-menu-item" data-active={i === menu.index}>
              <span className="sx-tag-dot" style={{ background: t.couleur || "#94a3b8" }} />
              <span className="truncate">{t.titre}</span>
            </button>
          ))}
        </div>
      ), document.body)}
    </div>
  );
});

RichMarkdownEditor.displayName = "RichMarkdownEditor";
